import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";
import { useHighlight, shouldHighlightText, isHighlightTarget, getMergedHighlightStyle } from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";
// import { store } from "lib/redux/store";
import { capitalize } from "../common/capitalize";

const MISTAKE_TEXT_COLOR = "#dc2626";
export const TemplateThirteenPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
  
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);


  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
    <div
    onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: spacing["4"],
        marginTop: spacing["10"],
        cursor:"pointer"
      }}
    >
      {heading && (
        <div
          style={{
            ...styles.flexCol,
            padding: spacing["1.5"],
            width: "100%",
            textAlign: "center",
            backgroundColor:`rgba(${r}, ${g}, ${b},.07)`,
            cursor: "pointer"
          }}
        >
            <Text
            
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {capitalize(heading)}
          </Text>
        </div>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing[2],
        
        }}
      >
        {children}
      </View>
    </div>
  );
};


export const ResumePDFDescriptionWithWhiteFont = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0,
  themeColor,
}: {
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
  themeColor?: string;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
 
  if (!items || items.length === 0) {
    return null; 
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {(enableHighlight || enableMistakeHighlight)
            ? renderNodeWithHighlight(node, section, itemIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)
            : renderNode(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
              themeColor="white"
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText themeColor="white">
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4,
                  paddingRight: 4, 
                  lineHeight: 1.3,
                }}
                bold={true}
                themeColor="white"
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                themeColor="white"
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

// Highlighting functions
/**
 * Render node with highlighting support 
 */
const renderNodeWithHighlight = (
  node: Node,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  highlight: ReturnType<typeof useHighlight>,
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);
  
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const textContent = element.textContent || "";
    const nodeName = element.nodeName.toLowerCase();
    
    const shouldHighlight = isTarget && 
      highlight.enabled && 
      highlight.mode === 'sentence' && 
      shouldHighlightText(textContent, highlight.texts, highlight.mode);
    
    switch (nodeName) {
      case "p":
        return (
          <View key={Math.random()}>
            <ResumePDFText 
              style={{ 
                lineHeight: 1.3,
                flexGrow: 1,
                ...(shouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {})
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }} themeColor={themeColor}>
                    {parseAndTransformHTMLWithHighlight((child as Element).innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        const liShouldHighlight = isTarget && 
          highlight.enabled && 
          highlight.mode === 'sentence' && 
          shouldHighlightText(textContent, highlight.texts, highlight.mode);
          
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
                ...(liShouldHighlight ? {
                  backgroundColor: highlight.style.backgroundColor,
                  color: highlight.style.color || highlight.style.textDecorationColor,
                  fontWeight: highlight.style.fontWeight as any,
                  textDecoration: highlight.style.textDecoration as any,
                  textDecorationStyle: highlight.style.textDecorationStyle as any,
                  textDecorationColor: highlight.style.textDecorationColor as any,
                } : {} )
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTMLWithHighlight(element.innerHTML, currentSection, currentItemIndex, themeColor, enableMistakeHighlight, mistakeAnalysis)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};

/**
 * Parse and transform HTML with highlighting support - preserves theme colors
 */
const parseAndTransformHTMLWithHighlight = (
  htmlString: string,
  currentSection: string,
  currentItemIndex: number | null,
  themeColor: string = "black",
  enableMistakeHighlight: boolean = false,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];
  const highlight = useHighlight();

  const isTarget = isHighlightTarget(currentSection, currentItemIndex, highlight);

  const transformNode = (node: Node, existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean } = {}) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const nodeName = element.nodeName.toLowerCase();

      let newStyles = { ...existingStyles };
      
      switch (nodeName) {
        case "strong":
        case "b":
          newStyles.bold = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "em":
        case "i":
          newStyles.italic = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "u":
          newStyles.underline = true;
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {Array.from(element.childNodes).map((child, idx) => (
                  <React.Fragment key={`${Math.random()}-${idx}`}>
                    {transformNode(child, { ...newStyles, underline: true })}
                  </React.Fragment>
                ))}
              </ResumePDFText>
            </ResumePDFLink>
          );
          
        case "span":
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
          
        default:
          return (
            <>
              {Array.from(element.childNodes).map((child, idx) => (
                <React.Fragment key={`${Math.random()}-${idx}`}>
                  {transformNode(child, newStyles)}
                </React.Fragment>
              ))}
            </>
          );
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      
      if (!text.trim()) return text;
      
      // Check for sentence-level mistakes FIRST
      const isSentenceMistakeMatch = !!(enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null &&
        isSentenceMistake(text, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData));
      
      // Priority 1: Handle sentence mistakes with all hover scenarios
      if (isSentenceMistakeMatch) {
        const isWordHover = isTarget && highlight.enabled && highlight.mode === 'word' && highlight.texts.length > 0;
        const isSentenceHover = isTarget && highlight.enabled && highlight.mode === 'sentence' && highlight.texts.length > 0 &&
          shouldHighlightText(text, highlight.texts, 'sentence');

        if (isWordHover) {
          // Sentence mistake + word hover: render with word highlights but force red for sentence
          return renderTextWithWordHighlights(
            text,
            highlight,
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            true,  // isHoverTarget
            enableMistakeHighlight,
            true   // forceRedText (sentence mistake)
          );
        } else if (isSentenceHover) {
          // Sentence mistake + sentence hover: yellow background + red text
          const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
          return (
            <ResumePDFText key={Math.random()} style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
              {text}
            </ResumePDFText>
          );
        } else {
          // Sentence mistake only: red text
          return (
            <ResumePDFText key={Math.random()} style={{ ...existingStyles, color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
              {text}
            </ResumePDFText>
          );
        }
      }

      // Priority 2: Check if text needs word-level mistake processing
      const needsWordProcessing = enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null;
      
      // If no hover, check for word mistakes
      if (!isTarget || !highlight.enabled) {
        if (needsWordProcessing) {
          // Word-level mistake detection without hover
          return renderTextWithWordHighlights(
            text,
            highlight,
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            false,  // isHoverTarget
            enableMistakeHighlight,
            false   // forceRedText
          );
        }
        
        // No mistakes, just apply styles if needed
        if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
          return (
            <ResumePDFText
              key={Math.random()}
              bold={existingStyles.bold}
              italic={existingStyles.italic}
              underline={existingStyles.underline}
              themeColor={themeColor}
            >
              {text}
            </ResumePDFText>
          );
        }
        return text;
      }
      
      // Priority 3: Handle hover highlighting (word mode)
      if (highlight.mode === 'word' && highlight.texts.length > 0) {
        return renderTextWithWordHighlights(
          text,
          highlight,
          existingStyles,
          themeColor,
          currentSection,
          currentItemIndex,
          mistakeAnalysis,
          true,  // isHoverTarget
          enableMistakeHighlight,
          false  // forceRedText
        );
      }
      
      // Priority 4: Handle hover highlighting (sentence mode)
      if (highlight.mode === 'sentence' && highlight.texts.length > 0) {
        const shouldHighlight = shouldHighlightText(text, highlight.texts, 'sentence');
        
        if (needsWordProcessing) {
          // Sentence hover + word-level mistakes: check both
          return renderTextWithWordHighlights(
            text,
            highlight,
            existingStyles,
            themeColor,
            currentSection,
            currentItemIndex,
            mistakeAnalysis,
            shouldHighlight,  // isHoverTarget
            enableMistakeHighlight,
            false  // forceRedText
          );
        }
        
        // Sentence hover only
        const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, shouldHighlight);
        return (
          <ResumePDFText
            key={Math.random()}
            style={mergedStyle}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      // No hover, no mistakes - apply styles if needed
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      
      return text;
    }
    
    return null;
  };

  doc.body.childNodes.forEach((node) => {
    const transformed = transformNode(node);
    if (transformed) {
      transformedChildren.push(transformed);
    }
  });

  return transformedChildren;
};

/**
 * Render text with word-level highlights - preserves theme colors
 */
const renderTextWithWordHighlights = (
  text: string,
  highlight: ReturnType<typeof useHighlight>,
  existingStyles: { bold?: boolean; italic?: boolean; underline?: boolean },
  themeColor: string = "black",
  currentSection?: string,
  currentItemIndex?: number | null,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  isHoverTarget: boolean = false,
  enableMistakeHighlight: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  try {
    const words = highlight.texts
      .map(w => (w || "").trim())
      .filter(Boolean)
      .map(w => {
        const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        return escaped;
      })
      .join("|");

    if (!words) {
      // No hover highlights, check for mistakes only
      if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && !forceRedText) {
        const textWords = text.split(/\s+/).filter(Boolean);
        const hasAnyMistake = textWords.some(word =>
          isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData!)
        );

        if (hasAnyMistake) {
          return (
            <>
              {textWords.map((word, i) => {
                const isMistake = isSingleWordMistake(word, currentSection, currentItemIndex ?? null, mistakeAnalysis.mistakeData!);
                const mergedStyle = isMistake
                  ? { ...existingStyles, color: MISTAKE_TEXT_COLOR }
                  : existingStyles;
                
                return (
                  <React.Fragment key={i}>
                    {i > 0 && " "}
                    {(mergedStyle.bold || mergedStyle.italic || mergedStyle.underline || isMistake) ? (
                      <ResumePDFText
                        bold={mergedStyle.bold}
                        italic={mergedStyle.italic}
                        underline={mergedStyle.underline}
                        style={isMistake ? { color: MISTAKE_TEXT_COLOR } : {}}
                        themeColor={themeColor}
                      >
                        {word}
                      </ResumePDFText>
                    ) : (
                      word
                    )}
                  </React.Fragment>
                );
              })}
            </>
          );
        }
      }
      
      if (forceRedText) {
        return (
          <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
            {text}
          </ResumePDFText>
        );
      }
      
      if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
        return (
          <ResumePDFText
            key={Math.random()}
            bold={existingStyles.bold}
            italic={existingStyles.italic}
            underline={existingStyles.underline}
            themeColor={themeColor}
          >
            {text}
          </ResumePDFText>
        );
      }
      return text;
    }

    const re = new RegExp(`\\b(${words})\\b`, "giu");
    const parts: Array<{ text: string; isHoverMatch: boolean }> = [];
    let lastIndex = 0;
    let match;

    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.substring(lastIndex, match.index), isHoverMatch: false });
      }
      parts.push({ text: match[0], isHoverMatch: true });
      lastIndex = re.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.substring(lastIndex), isHoverMatch: false });
    }
    
    if (parts.length === 0) {
      parts.push({ text, isHoverMatch: false });
    }
    
    // STEP 2: For each part, check for mistakes
    const finalParts: Array<{ text: string; isHoverMatch: boolean; isMistake: boolean }> = [];
    
    for (const part of parts) {
      if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && !part.isHoverMatch && part.text.trim()) {
        // Non-hover part: split by whitespace to check each word
        const wordParts = part.text.split(/(\s+)/);
        
        for (const wordPart of wordParts) {
          if (!wordPart) continue;
          
          const trimmed = wordPart.trim();
          const isMistake = trimmed.length > 0 && isSingleWordMistake(
            trimmed,
            currentSection,
            currentItemIndex ?? null,
            mistakeAnalysis.mistakeData
          );
          
          finalParts.push({
            text: wordPart,
            isHoverMatch: false,
            isMistake
          });
        }
      } else if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && currentSection && currentItemIndex !== null && part.isHoverMatch) {
        // Hover match: check if this word is also a mistake
        const isMistake = isSingleWordMistake(
          part.text.trim(),
          currentSection,
          currentItemIndex ?? null,
          mistakeAnalysis.mistakeData
        );
        
        finalParts.push({
          text: part.text,
          isHoverMatch: true,
          isMistake
        });
      } else {
        finalParts.push({
          text: part.text,
          isHoverMatch: part.isHoverMatch,
          isMistake: false
        });
      }
    }

    return (
      <>
        {finalParts.map((part, i) => {
          const isRed = forceRedText || part.isMistake;
          
          if (part.isHoverMatch && isHoverTarget) {
            // Hover match: apply yellow background
            const mergedStyle = getMergedHighlightStyle(existingStyles, highlight.style, true);
            if (isRed) {
              mergedStyle.color = MISTAKE_TEXT_COLOR;
            }
            return (
              <ResumePDFText key={i} style={mergedStyle} themeColor={themeColor}>
                {part.text}
              </ResumePDFText>
            );
          } else if (isRed) {
            // Mistake only: red text
            return (
              <ResumePDFText
                key={i}
                bold={existingStyles.bold}
                italic={existingStyles.italic}
                underline={existingStyles.underline}
                style={{ color: MISTAKE_TEXT_COLOR }}
                themeColor={themeColor}
              >
                {part.text}
              </ResumePDFText>
            );
          } else if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
            // Styling only
            return (
              <ResumePDFText
                key={i}
                bold={existingStyles.bold}
                italic={existingStyles.italic}
                underline={existingStyles.underline}
                themeColor={themeColor}
              >
                {part.text}
              </ResumePDFText>
            );
          } else {
            // Plain text
            return <React.Fragment key={i}>{part.text}</React.Fragment>;
          }
        })}
      </>
    );
  } catch (error) {
    if (forceRedText) {
      return (
        <ResumePDFText key={Math.random()} style={{ color: MISTAKE_TEXT_COLOR }} themeColor={themeColor}>
          {text}
        </ResumePDFText>
      );
    }
    
    if (existingStyles.bold || existingStyles.italic || existingStyles.underline) {
      return (
        <ResumePDFText
          key={Math.random()}
          bold={existingStyles.bold}
          italic={existingStyles.italic}
          underline={existingStyles.underline}
          themeColor={themeColor}
        >
          {text}
        </ResumePDFText>
      );
    }
    return text;
  }
};

export const ResumePDFDescription = ({ 
  items, 
  themeColor,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section = 'experience',
  itemIndex = 0
}: { 
  items: string[];
  themeColor?: string;
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number | null;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  
  if (!items || items.length === 0) {
    return null;
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {(enableHighlight || enableMistakeHighlight) ? renderNodeWithHighlight(node, section, itemIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis) : renderNode(node, themeColor)}
        </React.Fragment>
      ))}
    </View>
  );
};

//component for left column 
export const TemplateThirteenLeftColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading 
  const [firstChild, ...restChildren] = childrenArray;
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection  */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
              marginTop: spacing["10"],
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {firstChild && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
              marginTop: spacing["4"],
            }}
          >
            {firstChild}
          </View>
        )}
      </View>
      {/* Remaining subsections */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["2"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

//  section component for right column 
export const TemplateThirteenRightColumnSectionWithSubsections = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  
  if (childrenArray.length === 0) {
    return null;
  }

  // First child  grouped with heading
  const [firstChild, ...restChildren] = childrenArray;
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      {/* Heading + first subsection */}
      <View wrap={false} style={{ ...styles.flexCol }}>
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              // color: "white",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
              marginTop: spacing["10"],
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {firstChild && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
              marginTop: spacing["4"],
            }}
          >
            {firstChild}
          </View>
        )}
      </View>
      {/* Remaining subsections */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["2"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

// single-entry section for left column
export const TemplateThirteenLeftColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["4"],
          marginTop: spacing["10"],
        }}
      >
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};

//new single-entry section for right column
export const TemplateThirteenRightColumnSection = ({
  themeColor,
  heading,
  children,
}: {
  themeColor: string;
  heading?: string;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  let hex = themeColor.replace(/^#/, "");
  let r = parseInt(hex?.substring(0, 2), 16);
  let g = parseInt(hex?.substring(2, 4), 16);
  let b = parseInt(hex?.substring(4, 6), 16);

  return (
    <div
      onClick={handleClickCallback}
      style={{
        cursor: "pointer"
      }}
    >
      <View
        wrap={false} // Prevent orphan sections
        style={{
          ...styles.flexCol,
          gap: spacing["4"],
          marginTop: spacing["10"],
        }}
      >
        {heading && (
          <div
            style={{
              ...styles.flexCol,
              padding: spacing["1.5"],
              width: "100%",
              // color: "white",
              textAlign: "center",
              backgroundColor: `rgba(${r}, ${g}, ${b},.07)`,// "white" 
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: "white" ,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </div>
        )}
        {children && (
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["2"],
            }}
          >
            {children}
          </View>
        )}
      </View>
    </div>
  );
};
